import React, {useCallback, useEffect, useMemo, useState} from "react";
import BasicText from "../../components/form-v2/basic-text";
import {BlueButton} from "../../components/ui/Buttons";
import {CountrySelectSingle} from "../../components/form-v2/searchable-select/CountrySelectSingle";
import {useNavigate} from "react-router-dom";
import {Client, Department} from "@/types/userManagement";
import {useAuth} from "@/context/user";
import {ClientSelectV2} from "@/lib/clients/ClientSelectV2";
import {UserSelector} from "@/components/paginated-selector/user-selector";
import ModalV2 from "@/components/portal/ModalV2";
import {CoordinateSelector} from "@/components/map/coordinate-selector";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {AddressSearch} from "@/components/form-v2/searchable-select/AddressSearch";
import {GeocodeResponse} from "@/types/geocode";
import {CoordinateInput} from "@/components/form-v2/coordinate-input";
import {DepartmentSelectV2} from "@/lib/departments/DepartmentSelectV2";

export type FacilityFormData = {
    clientID: string | null;
    departmentID: string | null;
    name: string;
    countryISO?: string;
    region: string;
    county: string;
    town: string;
    postalCode: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    latitude: number;
    longitude: number;
    guardianIDs: string[];
};

type FacilityFormProps = {
    mode: "create" | "edit";
    initialData?: Partial<FacilityFormData>;
    initialDepartment?: Department | null;
    onSave: (data: FacilityFormData) => Promise<void>;
    saving: boolean;
    saved: boolean;
    errorMessage: string;
};

export const FacilityForm: React.FC<FacilityFormProps> = ({
    mode,
    initialData,
    initialDepartment,
    onSave,
    saving,
    saved,
    errorMessage,
}) => {


    const navigate = useNavigate();
    const {isSuperAdmin, isDepartmentAdmin, clientID: authclientID} = useAuth()

    // Ownership - initialize from initialData if in edit mode
    const [client, setClient] = useState<Client | null | undefined>(() => {
        if (mode === "edit" && initialData?.clientID) {
            // Create a placeholder client object for edit mode
            // The ClientSelectV2 will load the full client details
            return {
                id: initialData.clientID,
                name: "", // Will be populated by ClientSelectV2
            } as Client;
        }
        return undefined;
    });

    // Track if this is the first client change (to avoid clearing guardians on initial load)
    const [isFirstClientChange, setIsFirstClientChange] = useState(true);

    const [department, setDepartment] = useState<Department | null>(initialDepartment ?? null);

    const handleDepartmentChange = useCallback((d: Department | undefined) => {
        setDepartment(d ?? null);
        setGuardianIDs([]);
    }, []);

    // Handle client change - clear guardians and department when client changes (but not on initial load in edit mode)
    const handleClientChange = useCallback((newClient: Client | null | undefined) => {
        // Only clear if:
        // 1. This is not the first change OR we're in create mode
        // 2. AND the client ID actually changed
        if (!isFirstClientChange && newClient?.id !== client?.id) {
            setGuardianIDs([]);
            setDepartment(null);
        }
        setIsFirstClientChange(false);
        setClient(newClient);
    }, [client, isFirstClientChange]);

    // Facility fields
    const [name, setName] = useState(initialData?.name || "");

    // Address fields
    const [countryISO, setCountryISO] = useState<string | undefined>(initialData?.countryISO);
    const [region, setRegion] = useState(initialData?.region || "");
    const [county, setCounty] = useState(initialData?.county || "");
    const [town, setTown] = useState(initialData?.town || "");
    const [postalCode, setPostalCode] = useState(initialData?.postalCode || "");
    const [addressLine1, setAddressLine1] = useState(initialData?.addressLine1 || "");
    const [addressLine2, setAddressLine2] = useState(initialData?.addressLine2 || "");
    const [addressLine3, setAddressLine3] = useState(initialData?.addressLine3 || "");

    // Position fields
    const [latitude, setLatitude] = useState<number>(initialData?.latitude || 0);
    const [longitude, setLongitude] = useState<number>(initialData?.longitude || 0);

    // Guardians
    const [showGuardiansModal, setShowGuardiansModal] = useState(false);
    const [guardianIDs, setGuardianIDs] = useState<string[]>(initialData?.guardianIDs || []);

    // Map selector
    const [showMapSelector, setShowMapSelector] = useState(false);

    // Validation state
    const [validationError, setValidationError] = useState("");

    // Clear validation error when coordinates change
    useEffect(() => {
        if (validationError && (latitude !== 0 || longitude !== 0)) {
            setValidationError("");
        }
    }, [latitude, longitude, validationError]);

    const clientID = useMemo(() => {
        if (!isSuperAdmin) {
            return authclientID;
        }
        return client?.id

    }, [isSuperAdmin, client, authclientID]);

    const handleAddressSelect = useCallback((address: GeocodeResponse) => {
        setCountryISO(address.countryISO);
        setRegion(address.region);
        setCounty(address.county);
        setTown(address.town);
        setPostalCode(address.postalCode);
        setAddressLine1(address.addressLines[0] || "");
        setAddressLine2(address.addressLines[1] || "");
        setAddressLine3(address.addressLines[2] || "");
        setLatitude(address.latitude);
        setLongitude(address.longitude);
    }, []);

    const handleCoordinateConfirm = useCallback((lat: number, lng: number, address?: GeocodeResponse) => {
        setLatitude(lat);
        setLongitude(lng);
        setValidationError("");
        if (address) {
            handleAddressSelect(address);
        }
    }, [handleAddressSelect]);

    const handleSave = useCallback(async () => {
        // Clear previous validation errors
        setValidationError("");

        // Department admins must assign a department on creation
        if (mode === "create" && isDepartmentAdmin && !department) {
            setValidationError("Please select a department.");
            return;
        }

        // Validate that coordinates are provided
        if (latitude === 0 && longitude === 0) {
            setValidationError("Please provide valid coordinates. You can search for an address, select on the map, or paste coordinates.");
            return;
        }

        // Validate latitude range
        if (latitude < -90 || latitude > 90) {
            setValidationError("Latitude must be between -90 and 90 degrees.");
            return;
        }

        // Validate longitude range
        if (longitude < -180 || longitude > 180) {
            setValidationError("Longitude must be between -180 and 180 degrees.");
            return;
        }

        const formData: FacilityFormData = {
            clientID: client ? client.id : null,
            departmentID: department ? department.id : null,
            name,
            countryISO,
            region,
            county,
            town,
            postalCode,
            addressLine1,
            addressLine2,
            addressLine3,
            latitude,
            longitude,
            guardianIDs,
        };

        await onSave(formData);
    }, [client, department, isDepartmentAdmin, name, countryISO, region, county, town, postalCode, addressLine1, addressLine2, addressLine3, latitude, longitude, guardianIDs, onSave]);

    return (
        <>
            <BasicText
                value={name}
                onChange={setName}
                label="Facility Name"
                required
                placeholder="Enter facility name"
            />
            {isSuperAdmin && (
                <ClientSelectV2 onChange={handleClientChange} initialID={initialData?.clientID} label="Client" required isDisabled={mode === "edit"}/>
            )}


            <div className="flex flex-col">
                    <label className="mb-1">
                        Department
                        {isDepartmentAdmin && <span className="text-dark-red"> *</span>}
                    </label>
                    <DepartmentSelectV2
                        initialID={initialData?.departmentID}
                        clientID={clientID}
                        onChange={handleDepartmentChange}
                        isDisabled={!clientID}
                    />
                {!clientID && (
                        <p className="text-xs text-light-grey mt-1">Select a client first</p>
                    )}
                </div>


            {clientID && (
                <>
                    <div className="flex flex-col">
                        <label className="mb-1">Guardians</label>
                        <button
                            type="button"
                            onClick={() => setShowGuardiansModal(true)}
                            className="h-10 w-full shadow-md rounded-md px-4 bg-white hover:bg-gray-50 flex items-center justify-between text-left"
                        >
                            <span className={guardianIDs.length === 0 ? "text-gray-400" : ""}>
                                {guardianIDs.length === 0
                                    ? "Select guardians"
                                    : `${guardianIDs.length} guardian${guardianIDs.length === 1 ? "" : "s"} selected`
                                }
                            </span>
                            <span className="text-gray-400">▼</span>
                        </button>
                    </div>

                    <ModalV2 open={showGuardiansModal} setOpen={setShowGuardiansModal} className="pt-10">
                        <UserSelector ids={guardianIDs} onChange={setGuardianIDs} clientID={clientID}
                                      departmentID={department?.id}/>
                    </ModalV2>
                </>
            )}

            <div className="border-t pt-4">
                <h2 className="text-lg font-semibold mb-4">Address</h2>

                <div className="flex flex-col gap-4">
                    <AddressSearch
                        label="Search for an address"
                        onSelect={handleAddressSelect}
                    />

                    <BasicText
                        value={addressLine1}
                        onChange={setAddressLine1}
                        label="Address Line 1"
                        placeholder="Street address"
                    />

                    <BasicText
                        value={addressLine2}
                        onChange={setAddressLine2}
                        label="Address Line 2"
                        placeholder="Building, suite, etc."
                    />

                    <BasicText
                        value={addressLine3}
                        onChange={setAddressLine3}
                        label="Address Line 3"
                        placeholder="Additional address information"
                    />

                    <BasicText
                        value={town}
                        onChange={setTown}
                        label="Town/City"
                        placeholder="Enter town or city"
                    />

                    <BasicText
                        value={county}
                        onChange={setCounty}
                        label="County"
                        placeholder="Enter county"
                    />

                    <BasicText
                        value={region}
                        onChange={setRegion}
                        label="Region/State"
                        placeholder="Enter region or state"
                    />

                    <BasicText
                        value={postalCode}
                        onChange={setPostalCode}
                        label="Postal Code"
                        placeholder="Enter postal code"
                    />

                    <CountrySelectSingle
                        value={countryISO}
                        setValue={setCountryISO}
                        label="Country"
                    />
                </div>
            </div>

            <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Location Coordinates</h2>
                    <button
                        type="button"
                        onClick={() => setShowMapSelector(true)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                    >
                        Select on Map
                    </button>
                </div>

                <CoordinateInput
                    latitude={latitude}
                    longitude={longitude}
                    onLatitudeChange={setLatitude}
                    onLongitudeChange={setLongitude}
                    required
                />
                <ButtonV2 label="Select On Map" onClick={() => setShowMapSelector(true)}/>

            </div>

            <CoordinateSelector
                open={showMapSelector}
                setOpen={setShowMapSelector}
                initialLatitude={latitude}
                initialLongitude={longitude}
                onConfirm={handleCoordinateConfirm}
            />

            {validationError && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red/10 text-red text-sm">
                                        {validationError}
                </div>
            )}

            <div className="flex gap-4">
                <BlueButton
                    text={mode === "create" ? "Create Facility" : "Update Facility"}
                    onClick={handleSave}
                    loading={saving}
                />
                <button
                    className="px-4 py-2 border rounded-md hover:bg-gray-100"
                    onClick={() => navigate("/facilities")}
                    disabled={saving}
                >
                    Cancel
                </button>
            </div>

            {errorMessage && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red/10 text-red text-sm">
                    {errorMessage}
                </div>
            )}

            {saved && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-green/10 text-green text-sm">
                                        Facility {mode === "create" ? "created" : "updated"} successfully!
                </div>
            )}
        </>
    );
};
